/**
 * Excel-friendly CSV building, shared by the "Your data" exports.
 *
 * - UTF-8 byte-order mark so Excel opens £, accents and emoji correctly.
 * - CRLF line endings; fields quoted when they contain a comma, quote, line
 *   break or leading/trailing space, with quotes doubled.
 * - CSV formula injection guard: a text cell starting with = + - @ (or a tab /
 *   carriage return) is prefixed with an apostrophe so spreadsheet apps show it
 *   as text instead of running it as a formula. Real numbers are left alone.
 *
 * Pure functions with no browser APIs, so they're unit-tested directly.
 */

export const CSV_BOM = '﻿';

export type CsvValue = string | number | boolean | null | undefined;

export interface CsvColumn<Row> {
  header: string;
  value: (row: Row) => CsvValue;
}

const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;
const NEEDS_QUOTES = /[",\r\n]|^\s|\s$/;

/** Neutralise spreadsheet formulas in a text cell. */
export function guardFormula(text: string): string {
  return FORMULA_TRIGGERS.test(text) ? `'${text}` : text;
}

/** Turn one value into a safe, escaped CSV field. */
export function escapeCsvCell(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = guardFormula(String(value));
  return NEEDS_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Build a CSV document (without BOM) from rows and column definitions. */
export function toCsv<Row>(rows: readonly Row[], columns: readonly CsvColumn<Row>[]): string {
  const lines = [columns.map((c) => escapeCsvCell(c.header)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((c) => escapeCsvCell(c.value(row))).join(','));
  }
  return `${lines.join('\r\n')}\r\n`;
}

/** CSV document with the UTF-8 BOM prepended, ready to save as a .csv file. */
export function toCsvFile<Row>(rows: readonly Row[], columns: readonly CsvColumn<Row>[]): string {
  return CSV_BOM + toCsv(rows, columns);
}

// ---------------------------------------------------------------------------
// Date helpers — ISO formats Excel recognises.
// ---------------------------------------------------------------------------

const LONDON_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * A timestamp as `YYYY-MM-DD HH:mm:ss` in UK time (ISO 8601 with a space, which
 * Excel and Google Sheets read as a date-time). Empty for missing/invalid input.
 */
export function isoDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(LONDON_PARTS.formatToParts(date).map((p) => [p.type, p.value]));
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day} ${hour}:${parts.minute}:${parts.second}`;
}

/** A date column (`YYYY-MM-DD`) passed through, trimmed of any time part. */
export function isoDate(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : '';
}

/** A Postgres `time` (`HH:mm:ss`) as `HH:mm`. */
export function isoTime(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{2}:\d{2})/.exec(value);
  return match ? match[1] : '';
}
